/**
 * Assets Table Configuration
 *
 * Column widths migrated to central registry (Day 3 - 2025-10-19)
 * Location: src/config/columns/columnRegistry.ts
 *
 * Default settings and table-specific constants.
 */

import { generateStorageKey } from '@/utils/tableConfig.util';

/**
 * Default column order for assets table
 *
 * Ordering convention:
 * 1. Status - Always first when present (user can remove or reorder)
 * 2. Core columns - Identifier, Tags, Origin
 * 3. Date columns - First Seen, Last Seen always at end when present (user can remove or reorder)
 *
 * Note: This is the DEFAULT order. Users have full control to reorder or remove any column
 * except 'identifier' which is required.
 */
export const ASSET_DEFAULT_COLUMN_ORDER = [
  'status', // Convention: First by default
  'identifier', // Required: Cannot be removed
  'type', // Asset kind (Application / IP / Domain / Web Application / Other)
  'application', // Parent rollup-root annotation (sourced from Asset.group)
  'origins', // Source: Shown by default
  'created', // Convention: Last by default
  'visited', // Convention: Last by default
] as const;

// ============================================================================
// COLUMN GROUPING & BEHAVIOR
// ============================================================================

/**
 * Columns that support sorting
 * Columns not in this list will have sorting disabled
 *
 * IMPORTANT: These column IDs MUST match the backend's AllowedColumns map
 * in modules/chariot/backend/build/pkg/query/allowed_columns.go
 *
 * Backend validation will reject queries with invalid orderBy fields.
 */
export const SORTABLE_COLUMNS = [
  'status',
  'identifier',
  'asname',
  'asnumber',
  'attackSurface',
  'class',
  'country',
  'origins',
  'port',
  'protocol',
  'tags',
  'profile',
  'created',
  'visited',
] as const;

/**
 * Column ID that must always be visible (cannot be removed)
 */
export const REQUIRED_COLUMN_ID = 'identifier' as const;

// ============================================================================
// TABLE CONFIGURATION
// ============================================================================

/**
 * Storage key for persisting column configuration
 * v4: Centralized width config with proper type safety
 */
export const ASSET_TABLE_STORAGE_KEY = generateStorageKey(
  'assets',
  undefined,
  6
);

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Check if a column supports sorting
 *
 * @param columnId - The column identifier to check
 * @returns True if the column is in the sortable columns list
 */
export const isColumnSortable = (columnId: string): boolean => {
  return SORTABLE_COLUMNS.includes(
    columnId as (typeof SORTABLE_COLUMNS)[number]
  );
};
